import type {
  HouseholdModelPortfolioConfigurableDetailOptionKey,
  HouseholdModelPortfolioIpsFieldKey,
  HouseholdModelPortfolioTab,
} from './householdModelPortfolio.types';

export const HOUSEHOLD_MODEL_PORTFOLIO_TABS = {
  MODEL: 'model',
  IPS: 'ips',
} as const satisfies Record<string, HouseholdModelPortfolioTab>;

export const MODEL_PORTFOLIO_READ_PERMISSION = 'read:model_portfolios';
export const MODEL_PORTFOLIO_WRITE_PERMISSION = 'write:model_portfolios';
export const WORKFLOW_COMPLETION_WRITE_PERMISSION = 'write:workflow_completion';

/** Configurable Model tab booleans from organization-service widget options. */
export const MODEL_DETAIL_FIELD_OPTIONS = [
  'showModelStrategy',
  'showModelName',
  'showModelFamily',
  'showRiskProfile',
  'showRiskReturnLevel',
  'showSuggestible',
  'showZeroPercentCash',
  'showCashEquivalentSecurity',
  'showLowThresholdScore',
  'showMinimumScore',
  'showMaximumScore',
  'showForecastedRateOfReturn',
  'showForecastedStandardDeviation',
  'showFundamentalDetailsLink',
] as const satisfies readonly HouseholdModelPortfolioConfigurableDetailOptionKey[];

/** Defaults mirror organization-service `householdModelPortfolio` widget options. */
export const MODEL_DETAIL_FIELD_DEFAULTS = {
  showModelStrategy: true,
  showModelName: true,
  showModelFamily: true,
  showRiskProfile: true,
  showRiskReturnLevel: false,
  showSuggestible: false,
  showZeroPercentCash: false,
  showCashEquivalentSecurity: false,
  showLowThresholdScore: false,
  showMinimumScore: false,
  showMaximumScore: false,
  showForecastedRateOfReturn: true,
  showForecastedStandardDeviation: true,
  showFundamentalDetailsLink: false,
} as const satisfies Record<
  (typeof MODEL_DETAIL_FIELD_OPTIONS)[number],
  boolean
>;

export const HOUSEHOLD_MODEL_PORTFOLIO_CONFIGURABLE_DETAIL_FIELDS = [
  {
    optionKey: 'showModelStrategy',
    labelOptionKey: 'modelStrategyLabel',
    labelKey: 'modelPortfolios:strategy.label',
  },
  {
    optionKey: 'showModelName',
    labelOptionKey: 'modelNameLabel',
    labelKey: 'modelPortfolios:portfolio.details.name',
  },
  {
    optionKey: 'showModelFamily',
    labelOptionKey: 'modelFamilyLabel',
    labelKey: 'modelPortfolios:modelPortfolioTable.modelFamily',
  },
  {
    optionKey: 'showRiskProfile',
    labelKey: 'modelPortfolios:portfolioDetails.details.riskProfile',
  },
  {
    optionKey: 'showRiskReturnLevel',
    labelKey: 'modelPortfolios:portfolio.details.riskReturnLevel',
  },
  {
    optionKey: 'showSuggestible',
    labelKey: 'modelPortfolios:portfolio.details.suggestable',
  },
  {
    optionKey: 'showZeroPercentCash',
    labelKey: 'modelPortfolios:portfolioDetails.details.zeroPercentCash',
  },
  {
    optionKey: 'showCashEquivalentSecurity',
    labelKey: 'modelPortfolios:cashEquivalentSecurity',
  },
  {
    optionKey: 'showLowThresholdScore',
    labelKey: 'modelPortfolios:portfolio.details.lowThresholdScore',
  },
  {
    optionKey: 'showMinimumScore',
    labelKey: 'modelPortfolios:portfolio.details.minScore',
  },
  {
    optionKey: 'showMaximumScore',
    labelKey: 'modelPortfolios:portfolio.details.maxScore',
  },
  {
    optionKey: 'showForecastedRateOfReturn',
    labelKey: 'modelPortfolios:portfolio.details.forecastRateReturn',
  },
  {
    optionKey: 'showForecastedStandardDeviation',
    labelKey: 'modelPortfolios:portfolio.details.forecastStandardDeviation',
  },
  {
    optionKey: 'showFundamentalDetailsLink',
    labelKey: 'modelPortfolios:portfolioDetails.details.fundamentalDetailsLink',
  },
] as const satisfies ReadonlyArray<{
  optionKey: (typeof MODEL_DETAIL_FIELD_OPTIONS)[number];
  labelKey: string;
  labelOptionKey?: 'modelStrategyLabel' | 'modelNameLabel' | 'modelFamilyLabel';
}>;

export const HOUSEHOLD_MODEL_PORTFOLIO_EMPTY_DETAIL_VALUE = '—';

/** Read-only allocation table columns (labels from `modelPortfolios:securityTableColumns`). */
export const HOUSEHOLD_MODEL_PORTFOLIO_ALLOCATION_TABLE_COLUMN_KEYS = [
  'security',
  'assetClass',
  'taxRank',
  'targetPercentage',
  'portfolioPercentage',
] as const;

/** IPS tab show toggles from organization-service widget options. */
export const IPS_FIELD_OPTIONS = [
  'showDescription',
  'showInvestmentObjective',
  'showLiquidity',
  'showDisclaimer',
  'showTargetAllocations',
] as const satisfies readonly HouseholdModelPortfolioIpsFieldKey[];

/** Defaults mirror organization-service `householdModelPortfolio` widget options. */
export const IPS_FIELD_DEFAULTS = {
  showDescription: true,
  showInvestmentObjective: true,
  showLiquidity: false,
  showDisclaimer: false,
  showTargetAllocations: false,
} as const satisfies Record<(typeof IPS_FIELD_OPTIONS)[number], boolean>;

export const HOUSEHOLD_MODEL_PORTFOLIO_IPS_FIELDS = [
  {
    showOptionKey: 'showDescription',
    labelOptionKey: 'descriptionLabel',
    fallbackLabelKey: 'modelPortfolios:portfolio.policyAndAssets.description',
    kind: 'text',
    modelField: 'translatedPortfolioDescription',
  },
  {
    showOptionKey: 'showInvestmentObjective',
    labelOptionKey: 'investmentObjectiveLabel',
    fallbackLabelKey:
      'modelPortfolios:portfolio.policyAndAssets.investmentObjective',
    kind: 'text',
    modelField: 'translatedInvestmentObjective',
  },
  {
    showOptionKey: 'showLiquidity',
    labelOptionKey: 'liquidityLabel',
    fallbackLabelKey: 'modelPortfolios:portfolio.policyAndAssets.liquidity',
    kind: 'text',
    modelField: 'translatedLiquidity',
  },
  {
    showOptionKey: 'showDisclaimer',
    labelOptionKey: 'disclaimerLabel',
    fallbackLabelKey: 'modelPortfolios:portfolio.policyAndAssets.disclaimer',
    kind: 'richText',
    modelField: 'translatedPortfolioDisclaimer',
  },
  {
    showOptionKey: 'showTargetAllocations',
    labelOptionKey: 'targetAllocationsLabel',
    fallbackLabelKey:
      'modelPortfolios:portfolio.policyAndAssets.assetAllocation',
    kind: 'targetAllocations',
    modelField: 'targetAllocations',
  },
] as const;
